/**
 * Tiny DOM helpers. Kept minimal so the codebase doesn't accumulate a
 * homegrown framework over time.
 */

/** querySelector with a typed return. */
export function $<T extends Element = HTMLElement>(sel: string, root: ParentNode = document): T | null {
  return root.querySelector<T>(sel);
}

/** querySelectorAll → array. */
export function $$<T extends Element = HTMLElement>(sel: string, root: ParentNode = document): T[] {
  return Array.from(root.querySelectorAll<T>(sel));
}

/** Escape user-controlled strings for safe interpolation into HTML. */
export function escapeHtml(s: unknown): string {
  if (s === null || s === undefined) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** After adding a row to a scrollable items list (max-h + overflow-y-auto),
 *  bring the newly-added row into view — within the list's own scroll and the
 *  surrounding modal. Call right after the list re-renders. */
export function scrollItemsToBottom(containerId: string): void {
  requestAnimationFrame(() => {
    const c = document.getElementById(containerId);
    if (!c) return;
    c.scrollTop = c.scrollHeight;
    (c.lastElementChild as HTMLElement | null)?.scrollIntoView({ block: "nearest" });
  });
}

/** Tiny non-blocking toast notification. Auto-dismisses after ~2.4s. */
export function toast(msg: string, type: "ok" | "err" = "ok"): void {
  const root = document.getElementById("toastRoot");
  if (!root) return;
  const el = document.createElement("div");
  const color = type === "err" ? "#fecaca" : "#a7f3d0";
  const bg = type === "err" ? "rgba(127,29,29,.95)" : "rgba(6,78,59,.95)";
  el.style.cssText =
    `position:fixed;left:50%;bottom:24px;transform:translateX(-50%) translateY(20px);` +
    `padding:.6rem 1rem;border-radius:.6rem;background:${bg};color:${color};` +
    `font-size:.88rem;box-shadow:0 8px 24px rgba(0,0,0,.4);z-index:90;opacity:0;` +
    `transition:opacity .18s, transform .18s;pointer-events:none;`;
  el.textContent = msg;
  root.appendChild(el);
  // next tick → fade in
  requestAnimationFrame(() => {
    el.style.opacity = "1";
    el.style.transform = "translateX(-50%) translateY(0)";
  });
  setTimeout(() => {
    el.style.opacity = "0";
    el.style.transform = "translateX(-50%) translateY(20px)";
    setTimeout(() => el.remove(), 220);
  }, 2400);
}
